/**
 * Branded sign-in: the public "which methods" answer, as every sign-in page reads it (3 Nov 2026; contract
 * docs/superpowers/specs/2026-11-03-branded-sign-in-design.md section 4.4). Used by the admin page (builder 9) and the
 * portal and PhishTrainer pages (builder 14). The API (builder 6) answers with a logo, a name and methods only, never an
 * id, and the same bytes for every address of one e-mail domain.
 *
 * Frozen after wave 0 (coordinator). Both calls are public (skipAuth): nobody is signed in yet.
 */
import { fetchApi } from '@/lib/api/client'
import type { BrandTokens } from '@/lib/theme/app-themes/brand-theme'
import type { LoginCardStyle } from './card-styles'
import type { OrgMethodKey, SignInAudienceKey, SignInHostKind, SignInMethodKey } from './methods'

export interface SignInOptions {
  audience: SignInAudienceKey
  /** 'platform' = today's page (no company known, or the company changed nothing); 'company' = draw its card. */
  scope: 'platform' | 'company'
  /** The company's display name and logo; null when scope is 'platform'. */
  /** `logoDarkUrl`: the logo for a dark page, for every audience, brand switch or no. Absent from an older API. */
  company: { name: string; logoUrl: string | null; logoDarkUrl?: string | null; logoDarkIsCopy?: boolean } | null
  hostKind: SignInHostKind
  /** The methods to draw, in display order (SIGN_IN_PAGE_ORDER: the company's own apps before the shared ones). */
  offered: SignInMethodKey[]
  /** Switched on, but not available right now (A7). */
  unavailable: OrgMethodKey[]
  /**
   * The company's apps by their labels ("CSB Microsoft"): every offered one (its button) and every unavailable one (A7
   * names it). A label whose method is not in `offered` is never drawn as a button.
   */
  connections: Array<{ method: OrgMethodKey; label: string }>
  /** Admins: password and e-mail code are off, so show the emergency owner link (A4, A8). */
  emergencyOffered: boolean
  /**
   * This box's shared sign-in page origin (`https://dash.humanfirewall.ai`), for A6's "Go to dash...". Null when the
   * box does not know it; absent from an API older than this release.
   */
  platformUrl?: string | null
  /**
   * Employees on a company page (owner, 4 Nov 2026): the sign-in card the company chose under User Portal > Customise
   * Login, for the workspace this answer is for (the link's, else the company's only live one, else the company row):
   * the workspace's own where it overrides, else the company default, else 'corporate'. Absent for admins, PhishTrainer,
   * the platform answer and an API older than it: read absent as 'corporate'.
   */
  cardStyle?: LoginCardStyle
  /**
   * Employees on a company page (captain, 4 Nov 2026): what the company's admin set under User Portal > Customise Login
   * for the workspace this answer is for (the link's, else the company's only live one), under the portal settings' own
   * names, so the page on the company's address with no `?ws=` draws them as the admin's preview does. Absent with
   * several live workspaces and none in the link, for admins, PhishTrainer, the platform answer and an API older than
   * it: the page then keeps its defaults. A page that knows its workspace reads that workspace's settings instead.
   */
  loginPage?: SignInLoginPage | null
  /**
   * Employees on a company page, when the employee portal paints the company's brand (portal sign-in design, 4 Nov 2026):
   * the tokens the portal shell paints with after sign-in (the API's brand engine, both modes) and the logo it shows on a
   * dark page. Absent with no kit, the portal's brand switch off, another audience, and from an API older than it. Read
   * by the portal's sign-in only.
   */
  theme?: SignInTheme | null
}

/**
 * A workspace's Customise Login values, as the employee sign-in card reads them (PortalSignInSettings in
 * app/(portal)/portal/login/_components/portal-sign-in-card.tsx, the same names as the public portal settings).
 */
export interface SignInLoginPage {
  /** The welcome title as the admin typed it; null for none (the card's own default). */
  loginWelcomeTitle: string | null
  loginWelcomeSubtitle: string | null
  /**
   * The sign-in background: a public URL that names no id (an upload is served by
   * /v1/auth/sign-in-options/login-background, by the company address), a web address or path the admin gave, or null.
   */
  loginBackgroundImage: string | null
  loginPosition: 'Left' | 'Center' | 'Right'
  showCompanyLogoLogin: boolean
  showAdminLink: boolean
}

/** The portal's brand for a sign-in page. The tokens are the portal settings' own type (lib/user-portal/portal-brand.ts). */
export interface SignInTheme {
  light: BrandTokens
  dark: BrandTokens
  /** The logo for a dark page (an uploaded dark logo, else the white copy), or null: the light logo on its white plate. */
  logoDarkUrl: string | null
  /** It is the generated white copy (lib/portal/portal-logo.ts signInCardLogos keeps it off a picture). */
  logoDarkIsCopy: boolean
  /** The kit's primary colour as the kit holds it, per mode: decoration only (Brand Banner's header), never a button or text. */
  brand: { light: string; dark: string }
  /** The text colour that reads on `brand` (white or near-black, >= 4.5:1), per mode. */
  onBrand: { light: string; dark: string }
}

/**
 * By the host the page is served on (window.location.host in the browser; the request host on the server).
 * `workspaceId` (employees and PhishTrainer only): the workspace from the link (`?ws=`), as the portal knows it today.
 * A live company address wins over it; an unknown workspace gets the platform answer, byte-identical.
 */
export function fetchSignInOptions(
  audience: SignInAudienceKey,
  host: string,
  workspaceId?: string | null,
): Promise<SignInOptions> {
  const query = new URLSearchParams({ audience, host })
  if (workspaceId) query.set('workspaceId', workspaceId)
  return fetchApi<SignInOptions>(`/auth/sign-in-options?${query.toString()}`, { skipAuth: true })
}

/** By the typed work e-mail's DOMAIN (dash.humanfirewall.ai, A1). POST so the address stays out of URLs and logs. */
export function fetchSignInOptionsByEmail(
  audience: SignInAudienceKey,
  host: string,
  email: string,
): Promise<SignInOptions> {
  return fetchApi<SignInOptions>('/auth/sign-in-options/by-email', {
    method: 'POST',
    skipAuth: true,
    body: JSON.stringify({ audience, host, email }),
  })
}
